import { clients } from 'lib/https';
import { ConfigurationSettings } from '/models/configuration-settings';
import { KnowledgeBaseData, KnowledgeBaseFormData } from '../types/knowledge-base';

/**
 * Fetches the user's knowledge base data.
 *
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<KnowledgeBaseData | null>} The user's knowledge base data or null if not found.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const knowledgeBase = await getKnowledgeBase(config);
 */
export const getKnowledgeBase = async (config: ConfigurationSettings): Promise<KnowledgeBaseData | null> => {
  try {
    const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
    const url = `${baseUrl}${config.paths.taskforce.knowledgebase}`;
    
    
    const res = await clients.get<KnowledgeBaseData>(url, config.blocksKey);
    
    
    return res;
  } catch (error) {
    // If it's a 404 or similar, the user doesn't have knowledge base data yet
    console.warn('Knowledge base data not found or error fetching:', error);
    return null;
  }
};

/**
 * Creates or updates the user's knowledge base data.
 *
 * @param {KnowledgeBaseFormData} formData - The form data from the knowledge base modal.
 * @param {string} userId - The user ID to associate with the knowledge base.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<KnowledgeBaseResponse>} The response containing success status and any errors.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await upsertKnowledgeBase(formData, userId, config);
 */
export const upsertKnowledgeBase = async (
  formData: KnowledgeBaseFormData,
  userId: string,
  config: ConfigurationSettings
): Promise<KnowledgeBaseData> => {
  const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
  const url = `${baseUrl}${config.paths.taskforce.knowledgebase}`;

  // Transform form data to API format
  const apiData: KnowledgeBaseData = {
    user_nickname: formData.user_nickname,
    user_profession: formData.user_profession,
    llm_personality: formData.llm_personality,
    description: formData.description,
    is_enabled: formData.is_enabled,
    user_id: userId,
  };

  const res = await clients.post<KnowledgeBaseData>(
    url,
    config.blocksKey,
    JSON.stringify(apiData)
  );

  return res;
};

export const updateReferenceSetting = async (
  setting: 'reference_chat_history' | 'reference_saved_memories',
  value: boolean,
  config: ConfigurationSettings
): Promise<void> => {
  const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
  const url = `${baseUrl}${config.paths.taskforce.updateReferenceChatHistoryStatus}`;

  const apiData = {
    [setting]: value,
  };

  await clients.post(url, config.blocksKey, JSON.stringify(apiData));
};

// Keep the old functions for backward compatibility if needed elsewhere
export const updateReferenceChatHistory = async (
  referenceChatHistory: boolean,
  config: ConfigurationSettings
): Promise<void> => {
  return updateReferenceSetting('reference_chat_history', referenceChatHistory, config);
};

export const updateReferenceSavedMemories = async (
  referenceSavedMemories: boolean,
  config: ConfigurationSettings
): Promise<void> => {
  return updateReferenceSetting('reference_saved_memories', referenceSavedMemories, config);
};
/**
 * Update reference saved memories status
 *
 * @param {boolean} referenceSavedMemories - The status of the reference saved memories.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<void>} The response containing success status and any errors.
/**
 * Utility function to transform API data to form format
 *
 * @param {KnowledgeBaseData} apiData - Data from the API
 * @returns {KnowledgeBaseFormData} Data formatted for the form
 */
export const transformApiDataToForm = (apiData: KnowledgeBaseData): KnowledgeBaseFormData => {
  return {
    user_nickname: apiData.user_nickname || '',
    user_profession: apiData.user_profession || '',
    llm_personality: apiData.llm_personality || '',
    description: apiData.description || '',
    is_enabled: apiData.is_enabled !== undefined ? apiData.is_enabled : true,
    reference_chat_history: apiData.reference_chat_history !== undefined ? apiData.reference_chat_history : false,
    reference_saved_memories: apiData.reference_saved_memories !== undefined ? apiData.reference_saved_memories : false,

  };
};

/**
 * Utility function to check if knowledge base data is empty
 *
 * @param {KnowledgeBaseFormData} data - The form data to check
 * @returns {boolean} True if all fields are empty
 */
export const isKnowledgeBaseEmpty = (data: KnowledgeBaseFormData): boolean => {
  return !data.user_nickname && !data.user_profession && !data.llm_personality && !data.description;
};
